import PropTypes from 'prop-types';
import React, { useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { id as idLocale } from 'date-fns/locale';
import { parseISO } from 'date-fns';
import { Input, InputGroup, InputGroupText } from 'reactstrap';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import FormField, { isLocked } from './FormField';

registerLocale('id', idLocale);

// Controls built on FormField (see FORMS.md). Each one takes the same base
// props: label, name, mode, required, hint, error, span.

const sizeClass = size => (size === 'sm' ? 'form-control-sm' : undefined);
const joinClass = (...parts) => parts.filter(Boolean).join(' ');

/* ------------------------------------------------------------------ */
/* Text, number, textarea                                              */
/* ------------------------------------------------------------------ */

export const TextField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  type = 'text',
  placeholder,
  prefix,
  suffix,
  size,
  mode = 'editable',
  required,
  hint,
  error,
  span,
  className,
  inputProps,
  ...rest
}) => (
  <FormField
    label={label}
    name={name}
    mode={mode}
    required={required}
    hint={hint}
    error={error}
    span={span}
    className={className}
  >
    {({ invalid, required: isRequired, ...control }) => {
      const input = (
        <Input
          {...control}
          type={type}
          value={value ?? ''}
          onChange={onChange}
          onBlur={onBlur}
          // A locked field shows no placeholder: an empty locked field means
          // "no value", not "type here".
          placeholder={isLocked(mode) ? undefined : placeholder}
          invalid={invalid}
          required={isRequired}
          bsSize={size}
          className={joinClass(mode === 'calculated' && 'hy-calculated')}
          autoComplete='off'
          {...inputProps}
          {...rest}
        />
      );
      if (!prefix && !suffix) return input;
      return (
        <InputGroup size={size} className={invalid ? 'has-validation' : ''}>
          {prefix && <InputGroupText>{prefix}</InputGroupText>}
          {input}
          {suffix && <InputGroupText>{suffix}</InputGroupText>}
        </InputGroup>
      );
    }}
  </FormField>
);

export const TextAreaField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  rows = 3,
  maxLength,
  mode = 'editable',
  required,
  hint,
  error,
  span,
  className,
  ...rest
}) => (
  <FormField
    label={label}
    name={name}
    mode={mode}
    required={required}
    hint={hint}
    error={error}
    span={span}
    className={className}
  >
    {({ invalid, required: isRequired, ...control }) => (
      <>
        <Input
          {...control}
          type='textarea'
          rows={rows}
          value={value ?? ''}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={isLocked(mode) ? undefined : placeholder}
          maxLength={maxLength}
          invalid={invalid}
          required={isRequired}
          className={joinClass(mode === 'calculated' && 'hy-calculated')}
          {...rest}
        />
        {maxLength && mode === 'editable' && (
          <div className='hy-field__counter' aria-hidden='true'>
            {String(value ?? '').length} / {maxLength}
          </div>
        )}
      </>
    )}
  </FormField>
);

/* ------------------------------------------------------------------ */
/* Select / autocomplete                                               */
/* ------------------------------------------------------------------ */

const flattenOptions = options =>
  options.flatMap(option => (option.options ? option.options : [option]));

const sameValue = (a, b) => String(a) === String(b);

const selectPortal = {
  menuPortalTarget: typeof document === 'undefined' ? undefined : document.body,
  styles: { menuPortal: base => ({ ...base, zIndex: 2000 }) },
};

// Static lists: pass `options` ([{ value, label }]) and the raw `value`
// (an id, or an array of ids when `multi`). onChange(value, option) gets the
// raw value back; a cleared single select gives null.
//
// Autocomplete from the server: pass `loadOptions(input) => Promise<option[]>`.
// Then `value` is the option object itself (the list is not known up front)
// and onChange(option) receives the option object.
export const SelectField = ({
  label,
  name,
  options = [],
  value,
  onChange,
  onBlur,
  multi = false,
  searchable = true,
  clearable,
  placeholder = 'Select…',
  loadOptions,
  noOptionsMessage,
  isLoading,
  size,
  mode = 'editable',
  required,
  hint,
  error,
  span,
  className,
  ...rest
}) => {
  const remote = typeof loadOptions === 'function';

  const flat = flattenOptions(options);
  let selected;
  if (remote) {
    selected = value ?? (multi ? [] : null);
  } else if (multi) {
    const wanted = Array.isArray(value) ? value : [];
    selected = flat.filter(o => wanted.some(v => sameValue(v, o.value)));
  } else {
    selected =
      value === null || value === undefined || value === ''
        ? null
        : (flat.find(o => sameValue(o.value, value)) ?? null);
  }

  const handleChange = picked => {
    if (remote) return onChange?.(picked);
    if (multi) {
      const list = picked || [];
      return onChange?.(
        list.map(o => o.value),
        list
      );
    }
    return onChange?.(picked ? picked.value : null, picked);
  };

  return (
    <FormField
      label={label}
      name={name}
      mode={mode}
      required={required}
      hint={hint}
      error={error}
      span={span}
      className={className}
    >
      {({ id, invalid, disabled, readOnly, ...aria }) => {
        if (readOnly) {
          // react-select has no read-only state; show the chosen label(s) in
          // a read-only input so it matches every other locked field.
          const shown = [].concat(selected || []).map(o => o.label);
          return (
            <Input
              id={id}
              name={name}
              readOnly
              value={shown.join(', ')}
              bsSize={size}
              className={joinClass(mode === 'calculated' && 'hy-calculated')}
              aria-describedby={aria['aria-describedby']}
            />
          );
        }
        const Component = remote ? AsyncSelect : Select;
        return (
          <Component
            {...selectPortal}
            inputId={id}
            name={name}
            className={joinClass(
              'hy-select',
              invalid && 'hy-select--invalid',
              size === 'sm' && 'hy-select--sm',
              disabled && 'hy-select--disabled'
            )}
            classNamePrefix='hy-select'
            options={remote ? undefined : options}
            loadOptions={remote ? loadOptions : undefined}
            defaultOptions={remote ? true : undefined}
            value={selected}
            onChange={handleChange}
            onBlur={onBlur}
            isMulti={multi}
            isSearchable={searchable}
            isClearable={clearable ?? !required}
            isDisabled={disabled}
            isLoading={isLoading}
            closeMenuOnSelect={!multi}
            placeholder={placeholder}
            noOptionsMessage={noOptionsMessage || (() => 'No results')}
            aria-invalid={invalid || undefined}
            aria-errormessage={invalid ? `${id}-error` : undefined}
            {...rest}
          />
        );
      }}
    </FormField>
  );
};

/* ------------------------------------------------------------------ */
/* Date pickers                                                        */
/* ------------------------------------------------------------------ */

const PICKERS = {
  date: {
    dateFormat: 'dd/MM/yyyy',
    placeholder: 'dd/mm/yyyy',
    icon: 'ri-calendar-line',
  },
  month: {
    dateFormat: 'MMMM yyyy',
    placeholder: 'Select month',
    icon: 'ri-calendar-line',
    showMonthYearPicker: true,
  },
  datetime: {
    dateFormat: 'dd/MM/yyyy HH:mm',
    placeholder: 'dd/mm/yyyy hh:mm',
    icon: 'ri-calendar-event-line',
    showTimeSelect: true,
    timeFormat: 'HH:mm',
    timeIntervals: 15,
    timeCaption: 'Time',
  },
  time: {
    dateFormat: 'HH:mm',
    placeholder: 'hh:mm',
    icon: 'ri-time-line',
    showTimeSelect: true,
    showTimeSelectOnly: true,
    timeFormat: 'HH:mm',
    timeIntervals: 15,
    timeCaption: 'Time',
  },
};

// Accepts a Date, an ISO string or a timestamp. Date-only strings are read as
// local dates so "2026-03-01" never slips to the previous day.
export const toDate = value => {
  if (!value) return null;
  const date =
    typeof value === 'string' ? parseISO(value) : new Date(value.valueOf());
  return Number.isNaN(date.getTime()) ? null : date;
};

// One date control for the whole app. onChange(date | null) always gets a
// Date (or null when cleared); convert to the API format at the call site.
export const DateField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  picker = 'date',
  minDate,
  maxDate,
  placeholder,
  clearable,
  locale = 'id',
  size,
  mode = 'editable',
  required,
  hint,
  error,
  span,
  className,
  ...rest
}) => {
  const config = PICKERS[picker] || PICKERS.date;
  const { placeholder: defaultPlaceholder, icon, ...pickerProps } = config;

  return (
    <FormField
      label={label}
      name={name}
      mode={mode}
      required={required}
      hint={hint}
      error={error}
      span={span}
      className={className}
    >
      {({ id, invalid, disabled, readOnly, ...aria }) => (
        <div className='hy-date'>
          <DatePicker
            {...pickerProps}
            id={id}
            name={name}
            selected={toDate(value)}
            onChange={date => onChange?.(date)}
            onBlur={onBlur}
            minDate={toDate(minDate) || undefined}
            maxDate={toDate(maxDate) || undefined}
            placeholderText={
              isLocked(mode) ? undefined : placeholder || defaultPlaceholder
            }
            isClearable={!isLocked(mode) && (clearable ?? !required)}
            disabled={disabled}
            readOnly={readOnly}
            locale={locale}
            autoComplete='off'
            portalId='hy-datepicker-portal'
            popperPlacement='bottom-start'
            wrapperClassName='w-100'
            className={joinClass(
              'form-control',
              sizeClass(size),
              invalid && 'is-invalid',
              mode === 'calculated' && 'hy-calculated'
            )}
            aria-invalid={aria['aria-invalid']}
            aria-describedby={aria['aria-describedby']}
            aria-required={aria['aria-required']}
            {...rest}
          />
          <i className={`${icon} hy-date__icon`} aria-hidden='true' />
        </div>
      )}
    </FormField>
  );
};

/* ------------------------------------------------------------------ */
/* Checkbox, switch, radio group                                       */
/* ------------------------------------------------------------------ */

export const CheckField = ({
  label,
  name,
  checked,
  onChange,
  onBlur,
  switchStyle = false,
  mode = 'editable',
  required,
  hint,
  error,
  span,
  className,
}) => (
  <FormField
    name={name}
    mode={mode}
    required={required}
    hint={hint}
    error={error}
    span={span}
    className={className}
  >
    {({ id, invalid, required: isRequired, readOnly, disabled, ...aria }) => (
      <div
        className={joinClass(
          'form-check',
          switchStyle && 'form-switch form-switch-md'
        )}
      >
        <Input
          id={id}
          name={name}
          type='checkbox'
          role={switchStyle ? 'switch' : undefined}
          className={joinClass(invalid && 'is-invalid')}
          checked={Boolean(checked)}
          onChange={onChange}
          onBlur={onBlur}
          // Checkboxes have no readOnly; a locked box must not be toggled.
          disabled={disabled || readOnly}
          {...aria}
        />
        <label className='form-check-label' htmlFor={id}>
          {label}
          {isRequired && (
            <>
              <span className='hy-field__req' aria-hidden='true'>
                *
              </span>
              <span className='visually-hidden'> (required)</span>
            </>
          )}
        </label>
      </div>
    )}
  </FormField>
);

export const RadioGroupField = ({
  label,
  name,
  options = [],
  value,
  onChange,
  inline = true,
  mode = 'editable',
  required,
  hint,
  error,
  span,
  className,
}) => (
  <FormField
    label={label}
    name={name}
    mode={mode}
    required={required}
    hint={hint}
    error={error}
    span={span}
    className={className}
    group
  >
    {({ id, invalid, readOnly, disabled, ...aria }) => (
      <div className={joinClass('hy-radio-group', inline && 'is-inline')}>
        {options.map(option => {
          const optionId = `${id}-${option.value}`;
          return (
            <div className='form-check' key={option.value}>
              <Input
                id={optionId}
                type='radio'
                name={name}
                className={joinClass(invalid && 'is-invalid')}
                checked={sameValue(value, option.value)}
                onChange={() => onChange?.(option.value, option)}
                disabled={disabled || readOnly || option.disabled}
                aria-describedby={aria['aria-describedby']}
              />
              <label className='form-check-label' htmlFor={optionId}>
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
    )}
  </FormField>
);

// Password input with a show / hide toggle.
export const PasswordField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  autoComplete = 'current-password',
  mode = 'editable',
  required,
  hint,
  error,
  span,
  className,
}) => {
  const [visible, setVisible] = useState(false);
  return (
    <FormField
      label={label}
      name={name}
      mode={mode}
      required={required}
      hint={hint}
      error={error}
      span={span}
      className={className}
    >
      {({ invalid, required: isRequired, ...control }) => (
        <InputGroup>
          <Input
            {...control}
            type={visible ? 'text' : 'password'}
            value={value ?? ''}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={isLocked(mode) ? undefined : placeholder}
            invalid={invalid}
            required={isRequired}
            autoComplete={autoComplete}
          />
          <button
            type='button'
            className='btn btn-light border hy-password-toggle'
            onClick={() => setVisible(v => !v)}
            disabled={control.disabled}
            aria-label={visible ? 'Hide password' : 'Show password'}
            aria-pressed={visible}
          >
            <i
              className={visible ? 'ri-eye-off-line' : 'ri-eye-line'}
              aria-hidden='true'
            />
          </button>
        </InputGroup>
      )}
    </FormField>
  );
};

// Native file input with the shared label / hint / error. The chosen File is
// read from event.target.files in the caller's onChange; use `inputRef` to
// clear it after a reset.
export const FileField = ({
  label,
  name,
  onChange,
  accept,
  inputRef,
  mode = 'editable',
  required,
  hint,
  error,
  span,
  className,
}) => (
  <FormField
    label={label}
    name={name}
    mode={mode}
    required={required}
    hint={hint}
    error={error}
    span={span}
    className={className}
  >
    {({ invalid, required: isRequired, readOnly, ...control }) => (
      <Input
        {...control}
        innerRef={inputRef}
        type='file'
        accept={accept}
        onChange={onChange}
        invalid={invalid}
        required={isRequired}
        disabled={control.disabled || readOnly}
      />
    )}
  </FormField>
);

const baseFieldTypes = {
  label: PropTypes.node,
  name: PropTypes.string,
  mode: PropTypes.oneOf(['editable', 'readonly', 'disabled', 'calculated']),
  required: PropTypes.bool,
  hint: PropTypes.node,
  error: PropTypes.node,
  span: PropTypes.oneOf(['full', 2, 3]),
};

TextField.propTypes = {
  ...baseFieldTypes,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  type: PropTypes.string,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  size: PropTypes.oneOf(['sm']),
};
TextAreaField.propTypes = {
  ...baseFieldTypes,
  value: PropTypes.string,
  onChange: PropTypes.func,
  rows: PropTypes.number,
  maxLength: PropTypes.number,
};
SelectField.propTypes = {
  ...baseFieldTypes,
  options: PropTypes.array,
  onChange: PropTypes.func,
  multi: PropTypes.bool,
  loadOptions: PropTypes.func,
};
DateField.propTypes = {
  ...baseFieldTypes,
  onChange: PropTypes.func,
  picker: PropTypes.oneOf(['date', 'month', 'datetime', 'time']),
};
CheckField.propTypes = {
  ...baseFieldTypes,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  switchStyle: PropTypes.bool,
};
PasswordField.propTypes = {
  ...baseFieldTypes,
  value: PropTypes.string,
  onChange: PropTypes.func,
  autoComplete: PropTypes.string,
};
FileField.propTypes = {
  ...baseFieldTypes,
  onChange: PropTypes.func,
  accept: PropTypes.string,
};
RadioGroupField.propTypes = {
  ...baseFieldTypes,
  options: PropTypes.array,
  onChange: PropTypes.func,
  inline: PropTypes.bool,
};
